import React, { useState, useRef } from 'react';
import {
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SortIcon from '@mui/icons-material/Sort';
import GetAppIcon from '@mui/icons-material/GetApp';
import LocalPrintshopRoundedIcon from '@mui/icons-material/LocalPrintshopRounded';
import {
  TextField,
  InputAdornment,
  CircularProgress,
  Popover,
  Checkbox,
  FormControlLabel,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { RMSButton } from '../../../../shared/components/buttons/index';
import { hasMenuPermission } from 'features/evidence/hooks/useEvidenceNavPreferences';
import Evidence_PERMISSIONS from 'features/evidence/constants/evidencePermissionsConstants';
import htmlForPrinting from 'utils/htmlForPrinting';

const escapeHtml = (value) => {
  if (value === null || value === undefined) return '';

  return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
};

const ColumnsPanel = ({ columnOrdering, columnVisibility }) => {
  const { orderedColumns = [], setColumnOrder, reorderEnabled = false, toggleReorder } =
    columnOrdering || {};
  const { model = {}, setModel } = columnVisibility || {};

  const dragFieldRef = useRef(null);
  const [dragOverField, setDragOverField] = useState(null);
  const listableColumns = orderedColumns.filter(
    (col) => col.field !== 'actions' && col.field !== '__check__' && col.hideable !== false
  );

  const handleToggle = (field) => {
    const isCurrentlyVisible = model[field] !== false;
    setModel?.((prev) => ({
      ...prev,
      [field]: isCurrentlyVisible ? false : true,
    }));
  };

  const handleShowAll = () => {
    setModel?.((prev) => {
      const next = { ...prev };
      listableColumns.forEach((col) => {
        next[col.field] = true;
      });
      return next;
    });
  };

  const handleHideAll = () => {
    setModel?.((prev) => {
      const next = { ...prev };
      listableColumns.forEach((col) => {
        next[col.field] = false;
      });
      return next;
    });
  };
  const handleDragStart = (field) => {
    dragFieldRef.current = field;
  };

  const handleDragOver = (e, field) => {
    e.preventDefault();
    if (field !== dragOverField) setDragOverField(field);
  };

  const handleDrop = (targetField) => {
    const sourceField = dragFieldRef.current;
    dragFieldRef.current = null;
    setDragOverField(null);

    if (!sourceField || sourceField === targetField) return;

    setColumnOrder?.((prev) => {
      const safePrev =
        Array.isArray(prev) && prev.length ? prev : orderedColumns.map((c) => c.field);

      const newOrder = [...safePrev];
      const fromIndex = newOrder.indexOf(sourceField);
      const toIndex = newOrder.indexOf(targetField);

      if (fromIndex === -1 || toIndex === -1) return safePrev;

      newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, sourceField);

      return newOrder;
    });
  };

  return (
    <div className="columns-panel">
      <div className="columns-panel__actions">
        <RMSButton
          size="small"
          label="Show All"
          color="default"
          variant="text"
          onClick={handleShowAll}
        />
        <RMSButton
          size="small"
          label="Hide All"
          color="default"
          variant="text"
          onClick={handleHideAll}
        />
      </div>

      <RMSButton
        size="small"
        fullWidth
        variant={reorderEnabled ? 'contained' : 'outlined'}
        startIcon={<SortIcon fontSize="small" />}
        onClick={toggleReorder}
        className="columns-panel__sort-btn"
        label={reorderEnabled ? 'Column Sorting: On' : 'Column Sorting: Off'}></RMSButton>

      <Divider className="columns-panel__divider" />

      {listableColumns.map((col) => (
        <div
          key={col.field}
          draggable
          onDragStart={() => handleDragStart(col.field)}
          onDragOver={(e) => handleDragOver(e, col.field)}
          onDrop={() => handleDrop(col.field)}
          className={`columns-panel__row${
            dragOverField === col.field ? ' columns-panel__row--drag-over' : ''
          }`}>
          <DragIndicatorIcon fontSize="small" className="columns-panel__drag-icon" />
          <FormControlLabel
            className="columns-panel__checkbox-label"
            control={
              <Checkbox
                size="small"
                checked={model[col.field] !== false}
                onChange={() => handleToggle(col.field)}
              />
            }
            label={col.headerName || col.field}
          />
        </div>
      ))}
    </div>
  );
};

const CustomToolbar = ({
  // Toolbar Config
  toolbar = {
    showSearch: true,
    showColumnSelector: true,
    showFilter: true,
    showDensity: true,
    showExport: true,
  },

  searchInput,
  setSearchInput,
  onSearch,

  // Search
  search = {
    placeholder: 'Search...',
  },
  layoutSync,
  columnOrdering,
  columnVisibility,
  exportActions = [],
}) => {
  const {
    showSearch = true,
    showColumnSelector = true,
    showFilter = true,
    showDensity = true,
    showExport = true,
    showPrint = true,
    printTitle = 'Records',
  } = toolbar;
  const [anchorEl, setAnchorEl] = useState(null);
  const columnsOpen = Boolean(anchorEl);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const apiRef = useGridApiContext();

  const printReportPermission = hasMenuPermission({
    permissionLabel: Evidence_PERMISSIONS.Print_Reports,
    useAccessPermission: true,
  });
  const printIframeRef = useRef(null);

  const handleExportAction = (action) => {
    setExportAnchorEl(null);
    const visibleRows = Array.from(apiRef.current.getVisibleRowModels().values());
    const rows = action.maxRows ? visibleRows.slice(0, action.maxRows) : visibleRows;
    action.onClick?.(rows, visibleRows.length);
  };

  const getPrintableColumns = () =>
    apiRef.current
      .getVisibleColumns()
      .filter((col) => col.field !== 'actions' && col.field !== '__check__');

  const getPrintableCellValue = (column, row) => {
    const rawValue = column.valueGetter
      ? column.valueGetter({ id: row.id, field: column.field, row, api: apiRef.current })
      : row[column.field];

    const formatted = column.valueFormatter
      ? column.valueFormatter({
          id: row.id,
          field: column.field,
          value: rawValue,
          api: apiRef.current,
        })
      : rawValue;

    if (column.type === 'boolean' && typeof formatted === 'boolean') {
      return formatted ? 'YES' : 'NO';
    }

    return formatted ?? '';
  };

  const handlePrint = () => {
    const columns = getPrintableColumns();
    const rows = Array.from(apiRef.current.getVisibleRowModels().values());

    const tableHtml = `
      <h4>${escapeHtml(printTitle)}</h4>
      <table>
        <thead>
          <tr>${columns
            .map((col) => `<th>${escapeHtml(col.headerName || col.field)}</th>`)
            .join('')}</tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (row) =>
                `<tr>${columns
                  .map((col) => `<td>${escapeHtml(getPrintableCellValue(col, row))}</td>`)
                  .join('')}</tr>`
            )
            .join('')}
        </tbody>
      </table>
    `;

    const html = htmlForPrinting(tableHtml, printTitle);
    const win = printIframeRef.current.contentWindow;

    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <GridToolbarContainer className="toolbar-container">
      <div className="toolbar-container__left">
        {showSearch && (
          <TextField
            size="small"
            label={search.placeholder}
            value={searchInput}
            onChange={(e) => {
              const value = e.target.value;

              setSearchInput(value);

              if (!value.trim()) {
                onSearch('');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSearch(searchInput);
              }
            }}
            className="toolbar-container__search"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        )}
      </div>

      <div className="toolbar-container__right">
        {showColumnSelector && (
          <>
            <RMSButton
              size="small"
              startIcon={<ViewColumnIcon fontSize="small" />}
              onClick={(e) => setAnchorEl(e.currentTarget)}
              label="COLUMNS"
              variant="text"></RMSButton>

            <Popover
              open={columnsOpen}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
              <ColumnsPanel columnOrdering={columnOrdering} columnVisibility={columnVisibility} />
            </Popover>
          </>
        )}
        {showFilter && <GridToolbarFilterButton />}
        {showDensity && <GridToolbarDensitySelector />}
        {printReportPermission && showExport && exportActions.length > 0 && (
          <>
            <RMSButton
              size="small"
              startIcon={<GetAppIcon fontSize="small" />}
              onClick={(e) => setExportAnchorEl(e.currentTarget)}
              label="EXPORT"
              variant="text"
            />
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={() => setExportAnchorEl(null)}>
              {exportActions.map((action, index) => (
                <MenuItem key={index} onClick={() => handleExportAction(action)}>
                  {action.icon && <ListItemIcon>{action.icon}</ListItemIcon>}
                  <ListItemText>{action.label}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
        {showExport && exportActions.length === 0 && (
          <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
        )}
        {showPrint && (
          <RMSButton
            size="small"
            startIcon={<LocalPrintshopRoundedIcon fontSize="small" />}
            onClick={handlePrint}
            label="PRINT"
            variant="text"
          />
        )}
        {layoutSync?.enabled && (
          <>
            <RMSButton
              size="small"
              prefixIcon={
                layoutSync.saving ? <CircularProgress size={14} /> : <SaveIcon fontSize="small" />
              }
              disabled={layoutSync.saving}
              onClick={layoutSync.saveLayout}
              label="Save Layout"
            />
            <RMSButton
              size="small"
              prefixIcon={<RestartAltIcon fontSize="small" />}
              disabled={layoutSync.saving}
              onClick={layoutSync.resetLayout}
              label="Reset Layout"
            />
          </>
        )}
      </div>

      <iframe
        ref={printIframeRef}
        title="common-data-grid-print-frame"
        className="common-data-grid-print-iframe"
      />
    </GridToolbarContainer>
  );
};

export default CustomToolbar;
